import { useTranslation } from "react-i18next";
import FileTemplateSelect from "components/file-template-select/file-template-select";
import FileCategorySingleSelect from "components/file-category-single-select/file-category-single-select";
import { SeriesTypeCategoryFields } from "components/series-type-category-fields/series-type-category-fields";
import { Badge } from "./badge";

export type TemplateSource = "system" | "custom";

/** Where a generated document comes from and where it is filed. */
export interface TemplateSlotValue {
  source: TemplateSource;
  templateId: string;
  seriesId: string;
  fileTypeId: string;
  categoryId: string;
}

export interface TemplateSlotFieldsProps {
  source: TemplateSource;
  templateId: string;
  seriesId: string;
  fileTypeId: string;
  categoryId: string;
  onSourceChange: (source: TemplateSource) => void;
  onTemplateChange: (id: string) => void;
  onSeriesChange: (id: string) => void;
  onFileTypeChange: (id: string) => void;
  onCategoryChange: (id: string) => void;
  hideCategory?: boolean;
  configured?: boolean;
  /** Explains the series field, under it. */
  seriesHint?: string;
}

export interface TemplateSlotConfigProps extends TemplateSlotFieldsProps {
  label: string;
}

export function resolveTemplateSource(
  source: string | undefined,
  templateId: string | undefined
): TemplateSource {
  return (source as TemplateSource) || (templateId ? "custom" : "system");
}

/** A custom template slot needs a template; a system one always works. */
function isSlotConfigured({
  source,
  templateId,
  configured,
}: Pick<TemplateSlotFieldsProps, "source" | "templateId" | "configured">) {
  if (configured !== undefined) return configured;
  return source === "custom" ? Boolean(templateId) : true;
}

/** The choices for one generated document: system or own template, series, type, folder. */
export function TemplateSlotFields(props: TemplateSlotFieldsProps) {
  const {
    source,
    templateId,
    seriesId,
    fileTypeId,
    categoryId,
    onSourceChange,
    onTemplateChange,
    onSeriesChange,
    onFileTypeChange,
    onCategoryChange,
    hideCategory = false,
    seriesHint,
  } = props;
  const { t } = useTranslation();
  const configured = isSlotConfigured(props);

  return (
    <div className="space-y-3">
      {source === "custom" ? (
        <button
          type="button"
          className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
          onClick={() => {
            onSourceChange("system");
            onTemplateChange("");
          }}
        >
          {t("reset_to_system_template")}
        </button>
      ) : (
        <button
          type="button"
          className="text-sm font-medium text-indigo-600 hover:text-indigo-800"
          onClick={() => onSourceChange("custom")}
        >
          {t("use_custom_template")}
        </button>
      )}

      {!configured && (
        <p className="text-sm text-amber-800">
          {source === "custom"
            ? t("select_template")
            : t("series_file_type_category_required")}
        </p>
      )}

      {source === "custom" && (
        <div className="space-y-2">
          <FileTemplateSelect
            label=""
            onChange={(val: string) => {
              onTemplateChange(val);
              onSourceChange("custom");
            }}
            value={templateId}
            viewAll={true}
          />
          {!hideCategory && (
            <FileCategorySingleSelect
              value={categoryId ? [categoryId] : []}
              onChange={(value: string[]) => onCategoryChange(value?.[0] || "")}
              isRequired={false}
              subject="customer"
            />
          )}
        </div>
      )}

      {source === "system" && (
        <SeriesTypeCategoryFields
          seriesId={seriesId}
          fileTypeId={fileTypeId}
          categoryId={categoryId}
          onSeriesChange={onSeriesChange}
          onFileTypeChange={onFileTypeChange}
          onCategoryChange={onCategoryChange}
          hideCategory={hideCategory}
          seriesHint={seriesHint}
        />
      )}
    </div>
  );
}

export function TemplateSlotCard({
  label,
  ...fieldProps
}: TemplateSlotConfigProps) {
  const { t } = useTranslation();
  const configured = isSlotConfigured(fieldProps);

  return (
    <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-gray-900">{label}</span>
        <Badge tone={fieldProps.source === "custom" ? "amber" : "green"}>
          {fieldProps.source === "custom" ? t("custom") : t("system")}
        </Badge>
        <Badge tone={configured ? "green" : "amber"}>
          {configured ? t("configured") : t("required")}
        </Badge>
      </div>
      <TemplateSlotFields {...fieldProps} />
    </div>
  );
}
